import { Button } from "@progress/kendo-react-buttons";
import { Field } from "@progress/kendo-react-form";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { useEffect, useState } from "react";
import { sumByProperty } from "../../../utilities/objectUtilities";
import { FormSwitch } from "../../form";

interface IDealEntryPriceOverrideProps {
  quote: any;
  prices: any[];
  units: string;
  decimals?: number;
  priceValue: number;
  feeValue: number;
  isOverride: boolean;
  isParentBroker: boolean;
  loading: boolean;
  onRequote: (price: number, fee: number) => void;
  onChangeOverride: (requireRequote: boolean) => void;
}

export const DealEntryPriceOverride = ({
  quote,
  prices,
  units,
  decimals = 4,
  priceValue,
  feeValue,
  isOverride,
  isParentBroker,
  loading,
  onRequote,
  onChangeOverride,
}: IDealEntryPriceOverrideProps) => {
  const dollarsPerUnit = `($/${units})`;
  const totalVolume = sumByProperty(prices, "volume");
  const totalBrokerPrice = sumByProperty(quote, "brokerPrice");
  const totalRiskTakerPrice = sumByProperty(quote, "riskTakerPrice");
  const factor = totalVolume;
  const defaultUnitPrice = totalBrokerPrice / factor;
  const riskTakerUnitPrice = totalRiskTakerPrice / factor;
  const defaultBrokerFee = defaultUnitPrice - riskTakerUnitPrice;

  const [price, setPrice] = useState(priceValue);
  const [fee, setFee] = useState(feeValue);
  const [override, setOverride] = useState(isOverride);

  const handleRequote = (event: any) => {
    event.preventDefault();
    if (override) onRequote(price, fee);
    else onRequote(0, 0);
  };

  useEffect(() => {
    if (!override) {
      setPrice(0);
      setFee(0);
    }
    if (override && price === 0 && fee === 0) {
      setPrice(defaultUnitPrice);
      setFee(defaultBrokerFee);
    }
    onChangeOverride(override);
  }, [override, price, fee]);

  return (
    <div style={{ display: "flex", justifyContent: "right", gap: 10, margin: 10 }}>
      <div style={{ display: "flex", alignItems: "flex-end" }}>
        <Field
          label="Override"
          name="isOverride"
          component={FormSwitch}
          defaultChecked={override}
          disabled={loading}
          onChange={(e) => setOverride(e.value)}
        />
      </div>

      <div>
        <div>
          <div>Broker Fee</div>
          <div>{dollarsPerUnit}</div>
        </div>
        <NumericTextBox
          value={fee}
          onChange={(e) => setFee(e.value)}
          format={`c${decimals}`}
          width={100}
          spinners={false}
          disabled={!isParentBroker || loading || !override}
        />
      </div>

      <div>
        <div>
          <div>Unit Price</div>
          <div>{dollarsPerUnit}</div>
        </div>
        <NumericTextBox
          value={price}
          onChange={(e) => setPrice(e.value)}
          format={`c${decimals}`}
          width={100}
          spinners={false}
          disabled={loading || !override}
        />
      </div>

      <div style={{ display: "flex", alignItems: "flex-end" }}>
        <Tooltip anchorElement="target" parentTitle={true}>
          <Button
            size="small"
            icon="arrow-rotate-cw"
            themeColor="primary"
            title="Requote"
            onClick={handleRequote}
            disabled={loading}
          />
        </Tooltip>
      </div>
    </div>
  );
};
